import React, { useState } from "react";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import { PageLayout } from "../components/page-layout";
import MonteCarloChart from "../components/montecarlo-chart";

// ================================
// CONFIG
// ================================
const API_URL =
  "https://b4usjefnn7.execute-api.us-east-1.amazonaws.com/Prod/rentinvest";

// ================================
// PAGE
// ================================
export default function RentinvestPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    // Simulation horizon
    years: 10,

    // Property
    property_price: 800000,
    deposit: 100000,
    interest_rate: 0.06,
    property_growth: 0.02,
    mortgage_years: 30,

    // Rent
    rent_monthly: 3000,
    rent_inflation: 0.03,

    // Stocks
    stock_return: 0.10,
    stock_volatility: 0.1,
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: Number(e.target.value) });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          years: form.years,
          simulations: 1000, // always implicit
          paths_to_return: 50, // always implicit
          seed: 1, // always implicit

          property: {
            price: form.property_price,
            deposit: form.deposit,
            interest_rate: form.interest_rate,
            growth: form.property_growth,
            mortgage_years: form.mortgage_years,
          },
          rent: {
            monthly: form.rent_monthly,
            annual_inflation: form.rent_inflation,
          },
          stocks: {
            annual_return: form.stock_return,
            volatility: form.stock_volatility,
          },
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Erro na simulação");

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div style={{ padding: 20 }}>
        <Typography variant="h4" gutterBottom>
          Rent vs Invest – Property vs Stocks
        </Typography>

        {/* ================= SIMULATION HORIZON ================= */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">📅 Simulation Horizon</Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Years"
                  name="years"
                  type="number"
                  fullWidth
                  value={form.years}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ================= PROPERTY ================= */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">🏠 Property Assumptions</Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Property Price"
                  name="property_price"
                  type="number"
                  fullWidth
                  value={form.property_price}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Deposit"
                  name="deposit"
                  type="number"
                  fullWidth
                  value={form.deposit}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Interest Rate"
                  name="interest_rate"
                  type="number"
                  fullWidth
                  value={form.interest_rate}
                  onChange={handleChange}
                  inputProps={{ step: 0.001 }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Mortgage Years"
                  name="mortgage_years"
                  type="number"
                  fullWidth
                  value={form.mortgage_years}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  label="Property Growth"
                  name="property_growth"
                  type="number"
                  fullWidth
                  value={form.property_growth}
                  onChange={handleChange}
                  inputProps={{ step: 0.001 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ================= RENT ================= */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6">🏘️ Rent Assumptions</Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Monthly Rent"
                  name="rent_monthly"
                  type="number"
                  fullWidth
                  value={form.rent_monthly}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Rent Inflation"
                  name="rent_inflation"
                  type="number"
                  fullWidth
                  value={form.rent_inflation}
                  onChange={handleChange}
                  inputProps={{ step: 0.01 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ================= STOCKS ================= */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6">📈 Stock Assumptions</Typography>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Expected Annual Return"
                  name="stock_return"
                  type="number"
                  fullWidth
                  value={form.stock_return}
                  onChange={handleChange}
                  inputProps={{ step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Volatility"
                  name="stock_volatility"
                  type="number"
                  fullWidth
                  value={form.stock_volatility}
                  onChange={handleChange}
                  inputProps={{ step: 0.01 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ================= RUN ================= */}
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={loading}
        >
          Run Simulation
        </Button>

        {/* ================= STATUS ================= */}
        {loading && <CircularProgress sx={{ mt: 3 }} />}

        {error && (
          <Typography color="error" sx={{ mt: 3 }}>
            {error}
          </Typography>
        )}

        {/* ================= RESULTS ================= */}
        {result && (
          <>
            <Divider sx={{ my: 4 }} />

            {/* ===== SUMMARY ===== */}
            <Typography variant="h5" gutterBottom>
              Summary
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              {Object.entries(result.summary).map(([key, value]) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Card>
                    <CardContent>
                      <Typography variant="caption">{key}</Typography>
                      <Typography variant="h6">
                        {key === "stocks_win_rate_pct"
                          ? `${value.toLocaleString()}%`
                          : `$${value.toLocaleString()}`}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* ===== STOCKS DISTRIBUTION ===== */}
            <Typography variant="h5" gutterBottom>
              📊 Stocks Distribution
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={4} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption">P10</Typography>
                    <Typography variant="h6">
                      $
                      {result.timeseries.stocks.p10
                        .slice(-1)[0]
                        .toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption">P50</Typography>
                    <Typography variant="h6">
                      $
                      {result.timeseries.stocks.p50
                        .slice(-1)[0]
                        .toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4} md={2}>
                <Card>
                  <CardContent>
                    <Typography variant="caption">P90</Typography>
                    <Typography variant="h6">
                      $
                      {result.timeseries.stocks.p90
                        .slice(-1)[0]
                        .toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* ===== CHART ===== */}
            <Typography variant="h5" gutterBottom>
              Net Worth Over Time
            </Typography>

            <Card>
              <CardContent>
                <MonteCarloChart data={result} />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  );
}
